import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import Layout from '../../components/Layout'
import UserRow from '../../components/people/UserRow'
import PendingInvitationRow from '../../components/people/PendingInvitationRow'
import InviteModal from '../../components/people/InviteModal'
import AddGuideModal from '../../components/people/AddGuideModal'
import { ResultsOwnershipChartPlacement, PersonSummary, PendingInvitation, PeopleCounts, ROLE_OPTIONS } from '../../types'
import { LABELS } from '../AccountabilityCharts/labels'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  users: PersonSummary[]
  pending_invitations: PendingInvitation[]
  teams: { id: number; name: string }[]
  active_users: { id: number; name: string }[]
  filters: { search?: string; team_id?: string; role?: string; status?: string }
  counts: PeopleCounts
  can_invite: boolean
  can_manage: boolean
  assignable_roles: string[]
  restore_roles: string[]
  requires_user_approval: boolean
  pending_approval_count: number
  /** The org's single active Results Ownership Chart, or null when there is none. */
  results_ownership_chart: ResultsOwnershipChartPlacement | null
}

type Tab = 'all' | 'active' | 'pending' | 'deactivated' | 'deleted'

export default function PeopleIndex() {
  const { users, pending_invitations, teams, active_users, filters, counts, can_invite, can_manage, assignable_roles, restore_roles, requires_user_approval, pending_approval_count, results_ownership_chart } = usePage().props as unknown as Props
  const [tab, setTab] = useState<Tab>((filters.status as Tab) || 'active')
  const [search, setSearch] = useState(filters.search || '')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showAddGuideModal, setShowAddGuideModal] = useState(false)
  const [filterRole, setFilterRole] = useState(filters.role || '')
  const [filterTeam, setFilterTeam] = useState(filters.team_id || '')
  // View state only — deliberately not persisted (see task scope).
  const [showChartStatus, setShowChartStatus] = useState(false)
  const [onlyMissingFromChart, setOnlyMissingFromChart] = useState(false)

  const onChartCount = users.filter(u => u.on_chart).length
  const listedUsers = showChartStatus && onlyMissingFromChart
    ? users.filter(u => !u.on_chart)
    : users

  const applyFilters = (overrides: Record<string, string>) => {
    const params: Record<string, string> = {
      status: tab,
      search,
      role: filterRole,
      team_id: filterTeam,
      ...overrides,
    }
    Object.keys(params).forEach(k => { if (!params[k]) delete params[k] })
    router.get('/people', params, { preserveState: true, preserveScroll: true })
  }

  const handleTabChange = (newTab: Tab) => {
    setTab(newTab)
    applyFilters({ status: newTab })
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters({})
  }

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: counts.total },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'deactivated', label: 'Deactivated', count: counts.deactivated },
    ...(can_manage ? [{ key: 'deleted' as Tab, label: 'Removed', count: counts.deleted }] : []),
  ]

  return (
    <Layout>
      <Head title="People" />
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-primary font-heading">People</h1>
            <p className="text-sm text-text-secondary mt-0.5">{counts.total} total members in your organization</p>
          </div>
          {can_invite && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddGuideModal(true)}
                className="px-4 py-2 text-pinnacle-red text-sm font-semibold rounded-lg border border-pinnacle-red/30 hover:bg-pinnacle-red/10 transition-colors"
              >
                Add Guide
              </button>
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Invite People
              </button>
            </div>
          )}
        </div>

        {can_manage && requires_user_approval && pending_approval_count > 0 && (
          <Link
            href="/people/pending_approvals"
            className="flex items-center justify-between mb-4 px-4 py-3 bg-[#B45309]/10 text-[#B45309] rounded-lg text-sm font-medium hover:bg-[#B45309]/15 transition-colors"
          >
            <span>
              {pending_approval_count} {pending_approval_count === 1 ? 'person is' : 'people are'} waiting for approval to join.
            </span>
            <span className="font-semibold">Review &rarr;</span>
          </Link>
        )}

        <div className="flex items-center gap-1 mb-4 border-b border-stone/20">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                tab === t.key
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {t.label}
              <span className={`ml-1.5 text-sm px-1.5 py-0.5 rounded-full ${
                tab === t.key ? 'bg-charcoal/10 text-text-primary' : 'bg-stone/20 text-text-muted'
              }`}>
                {t.count}
              </span>
              {tab === t.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pinnacle-red" />
              )}
            </button>
          ))}
        </div>

        {tab !== 'pending' && (
          <div className="flex items-center gap-3 mb-4">
            <form onSubmit={handleSearch} className="flex-1 max-w-sm">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-9 pr-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
                />
              </div>
            </form>

            <select
              value={filterRole}
              onChange={e => { setFilterRole(e.target.value); applyFilters({ role: e.target.value }) }}
              className="border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
            >
              <option value="">All Roles</option>
              {ROLE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            <select
              value={filterTeam}
              onChange={e => { setFilterTeam(e.target.value); applyFilters({ team_id: e.target.value }) }}
              className="border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
            >
              <option value="">All Teams</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            {results_ownership_chart && (
              <label className="flex items-center gap-2 cursor-pointer select-none border border-stone/30 rounded-lg px-3 py-2 bg-white whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showChartStatus}
                  onChange={e => { setShowChartStatus(e.target.checked); if (!e.target.checked) setOnlyMissingFromChart(false) }}
                  className="w-4 h-4 rounded border-stone/30 text-pinnacle-red focus:ring-pinnacle-red/20"
                />
                <span className="text-sm text-text-primary">Show {LABELS.feature} status</span>
              </label>
            )}
          </div>
        )}

        {tab !== 'pending' && results_ownership_chart && showChartStatus && (
          <div className="flex items-center justify-between gap-3 mb-4 px-4 py-2.5 bg-stone/20 rounded-lg">
            <p className="text-sm text-text-primary">
              {onChartCount} of {users.length} {users.length === 1 ? 'user is' : 'users are'} on the {LABELS.feature}
            </p>
            {users.length > onChartCount && (
              <button
                onClick={() => setOnlyMissingFromChart(v => !v)}
                className="text-sm font-semibold text-pinnacle-red hover:underline whitespace-nowrap"
              >
                {onlyMissingFromChart ? 'Show everyone' : `Show only the ${users.length - onChartCount} missing`}
              </button>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl border border-stone/20 shadow-sm overflow-hidden">
          {tab === 'pending' ? (
            pending_invitations.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <svg className="w-10 h-10 text-text-primary/20 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-sm text-text-secondary">No pending invitations</p>
              </div>
            ) : (
              sortPeople(pending_invitations).map(inv => (
                <PendingInvitationRow key={inv.id} invitation={inv} canManage={can_manage} />
              ))
            )
          ) : (
            listedUsers.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <svg className="w-10 h-10 text-text-primary/20 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-sm text-text-secondary">
                  {showChartStatus && onlyMissingFromChart && users.length > 0
                    ? `Everyone listed here is already on the ${LABELS.feature}`
                    : search || filterRole || filterTeam
                    ? 'No people match your filters'
                    : tab === 'deleted' ? 'No removed people' : 'No people yet'}
                </p>
              </div>
            ) : (
              sortPeople(listedUsers).map(user => (
                <UserRow
                  key={user.id}
                  user={user}
                  canManage={can_manage}
                  managers={active_users}
                  restoreRoles={restore_roles}
                  assignableRoles={assignable_roles}
                  showChartStatus={showChartStatus}
                  chart={results_ownership_chart}
                />
              ))
            )
          )}
        </div>
      </div>

      {showInviteModal && (
        <InviteModal
          teams={teams}
          managers={active_users}
          assignableRoles={assignable_roles}
          chart={results_ownership_chart}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {showAddGuideModal && (
        <AddGuideModal onClose={() => setShowAddGuideModal(false)} />
      )}
    </Layout>
  )
}
